type ExceptionEvent = {
  event?: string
  properties?: Record<string, unknown>
}

const CLIENT_IGNORE_PATTERNS = [
  /^NEXT_REDIRECT$/,
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /__firefox__/i,
  /__gCrWeb/i,
  /window\.webkit\.messageHandlers/i,
  /DarkReader/i,
  /Java object is gone/i,
  /CustomEvent captured as exception/i,
  /Called on script loaded before session recording is available/i,
  /NotAllowedError: The request is not allowed by the user agent or the platform in the current context/i,
  /play method is not allowed by the user agent or the platform in the current context/i,
  /Failed to read a named property 'removeEventListener' from 'Window'/i,
  /Blocked a frame with origin .* from accessing a cross-origin frame/i,
]

const SERVER_IGNORE_PATTERNS = [/NEXT_REDIRECT/i]

function matchesPattern(message: string | undefined, patterns: RegExp[]) {
  if (!message) {
    return false
  }

  return patterns.some((pattern) => pattern.test(message))
}

function getClientExceptionMessage(event: ExceptionEvent) {
  const properties = event.properties ?? {}

  const exceptionMessage =
    properties.$exception_message ??
    (Array.isArray(properties.$exception_list) ? properties.$exception_list[0] : undefined)

  if (typeof exceptionMessage === 'string') {
    return exceptionMessage
  }

  // Current PostHog SDKs serialize exception messages as exception_list[].value.
  if (
    exceptionMessage &&
    typeof exceptionMessage === 'object' &&
    'value' in exceptionMessage &&
    typeof exceptionMessage.value === 'string'
  ) {
    return exceptionMessage.value
  }

  if (
    exceptionMessage &&
    typeof exceptionMessage === 'object' &&
    '$exception_message' in exceptionMessage &&
    typeof exceptionMessage.$exception_message === 'string'
  ) {
    return exceptionMessage.$exception_message
  }

  return undefined
}

function getServerExceptionCandidates(error: unknown) {
  if (error instanceof Error) {
    const errorWithMetadata = error as Error & {
      cause?: unknown
      code?: string | number
      digest?: string
    }

    return [
      error.name,
      error.message,
      error.stack,
      errorWithMetadata.digest,
      typeof errorWithMetadata.code === 'string' || typeof errorWithMetadata.code === 'number'
        ? String(errorWithMetadata.code)
        : undefined,
      errorWithMetadata.cause instanceof Error
        ? errorWithMetadata.cause.message
        : typeof errorWithMetadata.cause === 'string'
          ? errorWithMetadata.cause
          : undefined,
    ]
  }

  if (typeof error === 'string') {
    return [error]
  }

  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>

    return ['name', 'message', 'digest', 'code']
      .map((key) => record[key])
      .filter(
        (value): value is string | number => typeof value === 'string' || typeof value === 'number',
      )
      .map((value) => String(value))
  }

  return []
}

export function shouldIgnoreClientExceptionEvent(event: ExceptionEvent | null | undefined) {
  if (!event || event.event !== '$exception') {
    return false
  }

  return matchesPattern(getClientExceptionMessage(event), CLIENT_IGNORE_PATTERNS)
}

export function annotateClientExceptionEvent<T extends ExceptionEvent | null>(event: T): T | null {
  if (!event || event.event !== '$exception') {
    return event
  }

  if (shouldIgnoreClientExceptionEvent(event)) {
    return null
  }

  return {
    ...event,
    properties: {
      ...(event.properties ?? {}),
      error_source: 'client',
    },
  }
}

export function shouldIgnoreServerException(error: unknown) {
  return getServerExceptionCandidates(error).some((candidate) =>
    matchesPattern(candidate, SERVER_IGNORE_PATTERNS),
  )
}
